import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const countryKeys = {
  all: ['countries'],
  lists: () => [...countryKeys.all, 'list'],
  details: () => [...countryKeys.all, 'detail'],
  detail: (id) => [...countryKeys.details(), id],
};

export const useCountries = () => {
  return useQuery({
    queryKey: countryKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('countries');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useCountry = (id, options = {}) => {
  return useQuery({
    queryKey: countryKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`countries/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (countryData) => {
      const response = await apiClient.post('countries', countryData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: countryKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating country:', error);
    },
  });
};

export const useUpdateCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (countryData) => {
      const response = await apiClient.put(`countries/${countryData.uuid}`, countryData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(countryKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: countryKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating country:', error);
    },
  });
};

export const useDeleteCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`countries/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: countryKeys.lists() });
      queryClient.removeQueries({ queryKey: countryKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting country:', error);
    },
  });
};
